import { CommonModule } from '@angular/common';
import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewEncapsulation,
    inject,
} from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
    EMPTY,
    Subject,
    debounceTime,
    map,
    switchMap,
    takeUntil,
} from 'rxjs';
import { LookupOption, LookupTypeCodes } from '../../../../models/lookup.models';
import {
    ANY_SENIORITY_CODE,
    AssignmentsStepResult,
    WizardClientMatter,
    WizardInitialAssignment,
    WizardPreferred,
    WizardRequestedResource,
} from '../../../../models/request-wizard.models';
import { ResourceDto } from '../../../../models/resource.models';
import { LookupService } from '../../../../services/lookup.service';
import { ResourceService } from '../../../../services/resource.service';
import { formatResourceSubtitle } from '../../../../utils/resource-subtitle.utils';
import { RequestedResourcesEditorComponent } from '../../../shared/requested-resources-editor/requested-resources-editor.component';

/** Number of resource results to fetch for the per-matter assignment typeahead. */
const ASSIGNMENT_SEARCH_PAGE_SIZE = 25;

/** Display row for the per-matter assignment search dropdown. */
interface ResourceSuggestion {
  employeeId: string;
  fullName: string;
  /** Role/title stored on the assignment when this resource is added. */
  role: string;
  /** Secondary line shown under the name. */
  secondary: string;
  /** True when this resource was added to the Preferred Resources list. */
  isPreferred: boolean;
  /** Seniority lookup code, when known. */
  seniorityCode?: string;
  /** Human-readable seniority label, when known. */
  seniority?: string;
}

/** A requested seniority and how many of its slots the assignments fill. */
export interface SeniorityFulfillment {
  seniorityCode: string;
  label: string;
  requested: number;
  fulfilled: number;
}

/**
 * Assignments wizard step. Per-matter resource assignments (preferred resources
 * are surfaced as ready picks here), plus a breakdown of how the assigned
 * resources fulfil the per-seniority counts requested on the Resources step.
 */
@Component({
  selector: 'app-assignments-step',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatSelectModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    RequestedResourcesEditorComponent,
  ],
  templateUrl: './assignments-step.component.html',
})
export class AssignmentsStepComponent implements OnInit, OnChanges, OnDestroy {
  private fb = inject(FormBuilder);
  private resourceService = inject(ResourceService);
  private lookupService = inject(LookupService);

  @Input() mode: 'create' | 'edit' = 'create';
  @Input() initialResult: AssignmentsStepResult | null = null;
  /** Matters selected in the Client & Timing step; resources are assigned per matter. */
  @Input() availableMatters: WizardClientMatter[] = [];
  /** Preferred resources from the Resources step, surfaced as ready picks. */
  @Input() preferred: WizardPreferred[] = [];
  /** Per-seniority counts requested on the Resources step; scored against assignments. */
  @Input() requestedResources: WizardRequestedResource[] = [];

  @Output() resultChange = new EventEmitter<AssignmentsStepResult>();

  get assignmentsLabel(): string {
    return this.mode === 'edit' ? 'Assignments' : 'Initial Assignments';
  }

  form!: FormGroup;

  // Initial assignments
  assignments: WizardInitialAssignment[] = [];

  /** Seniority lookup options, for labelling the fulfilment breakdown. */
  seniorityOptions: LookupOption[] = [];

  /** Per-matter requested counts by seniority, keyed by clientMatterNumber. */
  matterRequested = new Map<string, WizardRequestedResource[]>();

  // Per-matter assignment search state, keyed by clientMatterNumber.
  expandedMatters = new Set<string>();
  matterSearchText = new Map<string, string>();
  matterResourceSuggestions = new Map<string, ResourceSuggestion[]>();
  matterResourceLoading = new Set<string>();
  matterResourceNotFound = new Set<string>();

  readonly assignmentTypeOptions = [
    { code: 'PRIMARY' as const, name: 'Primary' },
    { code: 'COVERAGE' as const, name: 'Coverage' },
  ];

  private destroy$ = new Subject<void>();
  private matterResourceQuery$ = new Subject<{ cmn: string; query: string }>();

  ngOnInit(): void {
    this.form = this.fb.group({});

    if (this.initialResult) {
      this.assignments = [...(this.initialResult.initialAssignments ?? [])];
    }

    this.syncMatterRequested();
    this.loadSeniorityOptions();
    this.setupMatterResourceTypeahead();
    this.emit();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const change = changes['availableMatters'];
    if (!change) return;

    // Keep the per-matter requested counts in step with the selected matters.
    this.syncMatterRequested();

    if (this.assignments.length > 0) {
      // Drop any matter selections that are no longer available, and remove
      // assignments left covering no matters at all.
      const availableSet = new Set(
        this.availableMatters.map((m) => m.clientMatterNumber),
      );
      const before = this.assignmentsKey(this.assignments);
      const next = this.assignments
        .map((a) => ({
          ...a,
          clientMatterNumbers: (a.clientMatterNumbers ?? []).filter((n) =>
            availableSet.has(n),
          ),
        }))
        .filter((a) => a.clientMatterNumbers.length > 0);
      if (this.assignmentsKey(next) !== before) {
        this.assignments = next;
      }
    }

    // ngOnInit handles the first emit; emit here only on later matter changes.
    if (!change.firstChange) {
      this.emit();
    }
  }

  /**
   * Rebuilds the per-matter requested map to match the currently available
   * matters: preserves existing rows, seeds from `initialResult` on first load,
   * and starts new matters empty (per-matter counts are opt-in). Matters no
   * longer selected are dropped.
   */
  private syncMatterRequested(): void {
    const seed = this.initialResult?.matterRequestedResources ?? {};
    const next = new Map<string, WizardRequestedResource[]>();
    for (const m of this.availableMatters) {
      const cmn = m.clientMatterNumber;
      const existing = this.matterRequested.get(cmn) ?? seed[cmn];
      next.set(
        cmn,
        existing && existing.length > 0 ? existing.map((r) => ({ ...r })) : [],
      );
    }
    this.matterRequested = next;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ===== Requested-count fulfilment =====

  /** Total resources requested across all seniority rows. */
  get requestedTotal(): number {
    return this.requestedResources.reduce((sum, r) => sum + (r.count || 0), 0);
  }

  /** Distinct resources assigned (each person counts once across matters). */
  get assignedTotal(): number {
    return this.assignments.length;
  }

  /** Slots filled across all rows (capped at what was requested). */
  get fulfilledTotal(): number {
    return this.fulfillmentBreakdown.reduce((sum, r) => sum + r.fulfilled, 0);
  }

  /** Assigned resources beyond what was requested, if any. */
  get extraAssignedCount(): number {
    return Math.max(0, this.assignedTotal - this.requestedTotal);
  }

  private loadSeniorityOptions(): void {
    this.lookupService
      .getLookupOptions(LookupTypeCodes.SENIORITY)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (options) => {
          this.seniorityOptions = options;
        },
        error: (error) => {
          console.error('Error loading seniority options:', error);
        },
      });
  }

  private seniorityLabel(seniorityCode: string): string {
    if (seniorityCode === ANY_SENIORITY_CODE) return 'Any';
    return (
      this.seniorityOptions.find((o) => o.value === seniorityCode)?.label ??
      seniorityCode
    );
  }

  /** Request-level fulfilment: Resources-step counts vs. all assignments. */
  get fulfillmentBreakdown(): SeniorityFulfillment[] {
    return this.computeFulfillment(this.requestedResources, this.assignments);
  }

  /**
   * Allocates the given assigned resources to the requested seniority rows:
   * specific-seniority rows are filled first by matching seniority, then any
   * leftover resources (extra, unknown, or non-matching) fill the "Any" rows.
   * Each resource counts toward at most one row.
   */
  private computeFulfillment(
    requested: WizardRequestedResource[],
    assigned: WizardInitialAssignment[],
  ): SeniorityFulfillment[] {
    // Tally assigned resources by seniority code (missing seniority => '').
    const available = new Map<string, number>();
    for (const a of assigned) {
      const code = a.seniorityCode || '';
      available.set(code, (available.get(code) ?? 0) + 1);
    }

    const rows: SeniorityFulfillment[] = requested.map((r) => ({
      seniorityCode: r.seniorityCode,
      label: this.seniorityLabel(r.seniorityCode),
      requested: r.count,
      fulfilled: 0,
    }));

    // Specific-seniority rows take matching resources first.
    for (const row of rows) {
      if (row.seniorityCode === ANY_SENIORITY_CODE) continue;
      const have = available.get(row.seniorityCode) ?? 0;
      const used = Math.min(row.requested, have);
      row.fulfilled = used;
      available.set(row.seniorityCode, have - used);
    }

    // Everything left over (including unknown seniorities) fills the Any rows.
    let remaining = 0;
    for (const count of available.values()) remaining += count;
    for (const row of rows) {
      if (row.seniorityCode !== ANY_SENIORITY_CODE) continue;
      const used = Math.min(row.requested, remaining);
      row.fulfilled = used;
      remaining -= used;
    }

    return rows;
  }

  // ===== Per-matter requested counts (independent from the Resources step) =====

  requestedForMatter(clientMatterNumber: string): WizardRequestedResource[] {
    return this.matterRequested.get(clientMatterNumber) ?? [];
  }

  matterRequestedTotal(clientMatterNumber: string): number {
    return this.requestedForMatter(clientMatterNumber).reduce(
      (sum, r) => sum + (r.count || 0),
      0,
    );
  }

  /** Compact "2 Senior · 1 Any" summary shown in the matter header. */
  matterRequestedSummary(clientMatterNumber: string): string {
    return this.requestedForMatter(clientMatterNumber)
      .map((r) => `${r.count} ${this.seniorityLabel(r.seniorityCode)}`)
      .join(' · ');
  }

  /** Receives the updated rows for a matter from the shared editor. */
  onMatterRequestedRowsChange(
    clientMatterNumber: string,
    rows: WizardRequestedResource[],
  ): void {
    this.matterRequested.set(clientMatterNumber, rows);
    this.form.markAsDirty();
    this.emit();
  }

  // ===== Initial assignments =====

  private assignmentsKey(assignments: WizardInitialAssignment[]): string {
    return JSON.stringify(
      assignments.map((a) => [a.employeeId, a.clientMatterNumbers]),
    );
  }

  private setupMatterResourceTypeahead(): void {
    this.matterResourceQuery$
      .pipe(
        debounceTime(300),
        takeUntil(this.destroy$),
        switchMap(({ cmn, query }) => {
          if (!query || query.trim().length < 3) {
            this.matterResourceLoading.delete(cmn);
            this.matterResourceSuggestions.set(cmn, []);
            this.matterResourceNotFound.delete(cmn);
            return EMPTY;
          }
          this.matterResourceLoading.add(cmn);
          return this.resourceService
            .searchAllResources({
              page: 1,
              pageSize: ASSIGNMENT_SEARCH_PAGE_SIZE,
              search: query,
              sortBy: 'name',
              sortDirection: 'asc',
            })
            .pipe(map((result) => ({ cmn, result })));
        }),
      )
      .subscribe(({ cmn, result }) => {
        this.matterResourceLoading.delete(cmn);
        // If the user has since cleared the box below the search threshold,
        // a stale response shouldn't overwrite the preferred-resource list.
        if ((this.matterSearchText.get(cmn) || '').trim().length < 3) return;
        if (result && result.items) {
          // Hide resources already assigned to this matter.
          const assignedIds = this.assignmentsForMatter(cmn).map(
            (a) => a.employeeId,
          );
          const suggestions = result.items
            .filter((r) => !assignedIds.includes(r.employeeId))
            .map((r) => this.toSuggestion(r));
          this.matterResourceSuggestions.set(cmn, suggestions);
          if (suggestions.length === 0) {
            this.matterResourceNotFound.add(cmn);
          } else {
            this.matterResourceNotFound.delete(cmn);
          }
        }
      });
  }

  private toSuggestion(resource: ResourceDto): ResourceSuggestion {
    return {
      employeeId: resource.employeeId,
      fullName: resource.fullName,
      role: resource.title || 'Unknown',
      secondary: formatResourceSubtitle(
        resource.email,
        resource.title,
        resource.seniority,
      ),
      isPreferred: this.isPreferred(resource.employeeId),
      seniorityCode: resource.seniorityCode,
      seniority: resource.seniority,
    };
  }

  isPreferred(employeeId: string): boolean {
    return this.preferred.some((p) => p.id === employeeId);
  }

  /**
   * Shows the preferred resources (not yet assigned to this matter) as ready
   * picks before the user types anything.
   */
  private showPreferredSuggestions(clientMatterNumber: string): void {
    const assignedIds = this.assignmentsForMatter(clientMatterNumber).map(
      (a) => a.employeeId,
    );
    this.matterResourceSuggestions.set(
      clientMatterNumber,
      this.preferred
        .filter((p) => !assignedIds.includes(p.id))
        .map((p) => ({
          employeeId: p.id,
          fullName: p.name,
          role: p.role,
          secondary: formatResourceSubtitle(p.email, p.role, p.seniority),
          isPreferred: true,
          seniorityCode: p.seniorityCode,
          seniority: p.seniority,
        })),
    );
    this.matterResourceNotFound.delete(clientMatterNumber);
  }

  toggleMatter(clientMatterNumber: string): void {
    if (this.expandedMatters.has(clientMatterNumber)) {
      this.expandedMatters.delete(clientMatterNumber);
    } else {
      this.expandedMatters.add(clientMatterNumber);
    }
  }

  isMatterExpanded(clientMatterNumber: string): boolean {
    return this.expandedMatters.has(clientMatterNumber);
  }

  /**
   * Assignments that cover the given matter, with preferred resources sorted
   * above non-preferred ones (insertion order preserved within each group).
   */
  assignmentsForMatter(clientMatterNumber: string): WizardInitialAssignment[] {
    return this.assignments
      .filter((a) => (a.clientMatterNumbers ?? []).includes(clientMatterNumber))
      .sort(
        (a, b) =>
          (this.isPreferred(a.employeeId) ? 0 : 1) -
          (this.isPreferred(b.employeeId) ? 0 : 1),
      );
  }

  onMatterResourceInput(clientMatterNumber: string, event: Event): void {
    const query = (event.target as HTMLInputElement).value;
    this.matterSearchText.set(clientMatterNumber, query);
    if (query.trim().length < 3) {
      // Below the search threshold: fall back to the preferred resources.
      this.matterResourceLoading.delete(clientMatterNumber);
      this.showPreferredSuggestions(clientMatterNumber);
      return;
    }
    this.matterResourceQuery$.next({ cmn: clientMatterNumber, query });
  }

  onMatterResourceFocus(clientMatterNumber: string): void {
    // Surface preferred resources as soon as the box is focused (empty query).
    if (
      (this.matterSearchText.get(clientMatterNumber) || '').trim().length < 3
    ) {
      this.showPreferredSuggestions(clientMatterNumber);
    }
  }

  onMatterResourceBlur(clientMatterNumber: string): void {
    setTimeout(
      () => this.matterResourceSuggestions.set(clientMatterNumber, []),
      200,
    );
  }

  /**
   * Assigns a resource to a matter. Reuses the resource's existing assignment
   * (adding this matter to it) so a person assigned to several matters remains
   * a single entry in the payload, or creates a new assignment otherwise.
   */
  addResourceToMatter(
    clientMatterNumber: string,
    resource: ResourceSuggestion,
  ): void {
    const existing = this.assignments.find(
      (a) => a.employeeId === resource.employeeId,
    );
    if (existing) {
      if (!(existing.clientMatterNumbers ?? []).includes(clientMatterNumber)) {
        this.assignments = this.assignments.map((a) =>
          a.employeeId === resource.employeeId
            ? {
                ...a,
                clientMatterNumbers: [
                  ...(a.clientMatterNumbers ?? []),
                  clientMatterNumber,
                ],
              }
            : a,
        );
      }
    } else {
      this.assignments = [
        ...this.assignments,
        {
          employeeId: resource.employeeId,
          name: resource.fullName,
          role: resource.role,
          assignmentTypeCode: 'PRIMARY',
          clientMatterNumbers: [clientMatterNumber],
          seniorityCode: resource.seniorityCode,
          seniority: resource.seniority,
        },
      ];
    }
    this.matterSearchText.set(clientMatterNumber, '');
    this.matterResourceSuggestions.set(clientMatterNumber, []);
    this.matterResourceNotFound.delete(clientMatterNumber);
    this.form.markAsDirty();
    this.emit();
  }

  /** Removes a resource from a matter, dropping the assignment if it covers no matters. */
  removeResourceFromMatter(
    clientMatterNumber: string,
    employeeId: string,
  ): void {
    this.assignments = this.assignments
      .map((a) =>
        a.employeeId === employeeId
          ? {
              ...a,
              clientMatterNumbers: (a.clientMatterNumbers ?? []).filter(
                (n) => n !== clientMatterNumber,
              ),
            }
          : a,
      )
      .filter((a) => (a.clientMatterNumbers ?? []).length > 0);
    this.form.markAsDirty();
    this.emit();
  }

  changeAssignmentType(employeeId: string, type: 'PRIMARY' | 'COVERAGE'): void {
    this.assignments = this.assignments.map((a) =>
      a.employeeId === employeeId ? { ...a, assignmentTypeCode: type } : a,
    );
    this.form.markAsDirty();
    this.emit();
  }

  private emit(): void {
    const matterRequestedResources: Record<string, WizardRequestedResource[]> =
      {};
    for (const [cmn, rows] of this.matterRequested) {
      matterRequestedResources[cmn] = rows;
    }
    this.resultChange.emit({
      initialAssignments: this.assignments,
      matterRequestedResources,
    });
  }
}
